import React, { useCallback, useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { getAllAdminUsers, getFullUserDetails, toggleTest, updateFarmReviewer } from "services";

import { changeLanguage, getLanguageFromLocalStorage, useCancelAsyncOnUnmount } from "utils";

import { GET_FIELD_PRACTICES, GET_PRACTICES_FERTILIZERS_MINERAL, GET_PRACTICES_FERTILIZERS_ORGANIC } from "consts";

import {
  AdminDescriptions,
  AdminHead,
  Anchor,
  Avatar,
  Col,
  Form,
  Link,
  Option,
  Row,
  Select,
  Skeleton,
  Switch,
  TagNormal,
  Message as message,
} from "components";

import { CustomTaskDrawer } from "pages/admin/verifications/components/drawers/CustomTaskDrawer";

export const FarmHeader = ({ farm, loadingFarm, userId, onUpdate, taskStatus, verificationSummary }) => {
  const [adminUsers, setAdminUsers] = useState([]);
  const [showCustomTaskDrawer, setShowCustomTaskDrawer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState();

  const signal = useCancelAsyncOnUnmount()();
  const queryClient = useQueryClient();

  // Available languages for fertiliser/crop translations
  const languageOptions = [
    { value: "en", label: "English" },
    { value: "de", label: "Deutsch" },
    { value: "cs-CZ", label: "Čeština" },
    { value: "sk-SK", label: "Slovenčina" },
    { value: "pl", label: "Polski" },
  ];

  const handleLanguageChange = (language) => {
    changeLanguage(language);
    // Invalidate all queries that contain translated data
    queryClient.invalidateQueries({ queryKey: [GET_FIELD_PRACTICES] });
    queryClient.invalidateQueries({ queryKey: [GET_PRACTICES_FERTILIZERS_MINERAL] });
    queryClient.invalidateQueries({ queryKey: [GET_PRACTICES_FERTILIZERS_ORGANIC] });
  };

  const handleSwitch = () => {
    toggleTest({ farmId: farm?.id }).then(() => () => onUpdate());
  };

  const handleSelectReviewer = (admin) => {
    setLoading(true);
    updateFarmReviewer({
      signal,
      farmId: farm?.id,
      reviewerId: admin,
    })
      .then((res) => {
        if (res.status === "success") {
          message({
            content: "The gremlins accepted your sacrifice! Nom nom nom",
            icon: "gremlin",
          });
        } else {
          message({
            content: "Something went wrong",
          });
        }
      })
      .finally(() => {
        setLoading(false);
        onUpdate();
      });
  };

  const loadAdminUsers = useCallback(() => {
    getAllAdminUsers({ signal }).then((res) => setAdminUsers(res.filter((user) => user.verifier)));
  }, [signal]);

  const loadUser = useCallback(() => {
    setLoading(true);
    if (userId) {
      getFullUserDetails({
        id: userId,
        signal,
      })
        .then((res) => setUser(res))
        .finally(() => setLoading(false));
    }
  }, [userId, signal]);

  const handleDrawerClose = () => {
    setShowCustomTaskDrawer(false);
  };

  useEffect(() => {
    loadUser();
    loadAdminUsers();
  }, [loadUser, loadAdminUsers, farm]);

  return (
    farm && (
      <>
        <AdminHead
          headline={
            <>
              {farm.name}
              {farm.verified !== false && taskStatus === "pending_verifications" ? (
                <TagNormal style={{ transform: "translate3d(0, -50%, 0) scale(0.9)", position: "absolute" }} color="yellow" text="pending verification" />
              ) : null}
              {farm.verified !== false && taskStatus === "missing_documents" ? (
                <TagNormal style={{ transform: "translate3d(0, -50%, 0) scale(0.9)", position: "absolute" }} color="red" text="missing documentation" />
              ) : null}
              {farm.verified !== false && verificationSummary && verificationSummary.verificationsStatus.pending === 0 ? (
                <TagNormal style={{ transform: "translate3d(0, -50%, 0) scale(0.9)", position: "absolute" }} color="blue" text="verify payout" />
              ) : null}
              {farm.verified === false ? (
                <TagNormal style={{ transform: "translate3d(0, -50%, 0) scale(0.9)", position: "absolute" }} color="red" text="reject payout" />
              ) : null}
            </>
          }
          link="/verifications/farms"
          tooltipText="Back to farms"
        />
        <Row className="margin-bottom-200">
          <Col>
            {!loading && !loadingFarm && farm ? (
              <AdminDescriptions
                items={[
                  {
                    label: "User",
                    span: 1,
                    children: user && user.name ? <Link to={`/user/${user.id}`}>{user.name}</Link> : "Unknown",
                  },
                  {
                    label: "Phone",
                    span: 1,
                    children: user && user.phone ? user.phone : "Unknown",
                  },
                  {
                    label: "Email",
                    span: 1,
                    children: (
                      <Anchor href={`mailto: ${user.email}`} mixpanelText="mailto">
                        {user.email}
                      </Anchor>
                    ),
                  },
                  {
                    label: "Test farm",
                    span: 1,
                    children: <Switch defaultChecked={farm.test} onChange={() => handleSwitch()} />,
                  },
                  {
                    label: "Reviewer",
                    span: 1,
                    children:
                      !loading && !loadingFarm ? (
                        <Form>
                          <Select className="verification-select" onChange={(e) => handleSelectReviewer(e)} defaultValue={farm.reviewerId || "none"}>
                            {adminUsers.map((user, i) => (
                              <Option value={user.id} key={i}>
                                <Avatar className="margin-right-50" size="xs" name={user.name} /> {user.name}
                              </Option>
                            ))}
                            <Option value="none">reviewer not selected</Option>
                          </Select>
                        </Form>
                      ) : (
                        <></>
                      ),
                  },
                  {
                    label: "Language",
                    span: 1,
                    children: (
                      <Form>
                        <Select className="verification-select" onChange={handleLanguageChange} defaultValue={getLanguageFromLocalStorage()}>
                          {languageOptions.map((option, i) => (
                            <Option value={option.value} key={i}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form>
                    ),
                  },
                ]}
              />
            ) : (
              <Skeleton type="paragraph" rows={3} />
            )}
          </Col>
        </Row>
        <CustomTaskDrawer visible={showCustomTaskDrawer} onClose={handleDrawerClose} farmId={farm?.id} farmCountry={farm?.country} />
      </>
    )
  );
};
